// Rendering rules for the unified company metric shape
// ({bucket, lowerBound, upperBound?, pointEstimate?}, spec 2026-09-23 §4/§5.1/§5.2).
// Wire form: absent members are null and lowerBound defaults to 0.
import {DollarFormatter, EmployeeFormatter} from "./formatting";

const EN_DASH = "–";

// A missing headcount or revenue is unreported at the source, not "N/A" (FE #749).
export const NOT_REPORTED = "Not reported";

// ETL #97 marks description-derived headcounts `estimated: true` (FE #749).
export const ESTIMATED_LABEL = "est.";
export const ESTIMATED_TOOLTIP = "Estimated from the company description";
export const isEstimatedMetric = (metric) => metric?.estimated === true;

// `<25` straddles 1–10 and 11–50, so it is not reported (owner ruling 2026-10-13,
// ETL #100). Mirrors the backend `format_company_metric` rule.
export const UNREPORTED_HEADCOUNT_BUCKET = "LT_25";
// Saved Chat artifacts can hold the text it rendered as before the ruling.
export const UNREPORTED_HEADCOUNT_TEXT = "<25";
/** @param {unknown} value */
export const isUnreportedHeadcountText = (value) =>
  typeof value === "string" && value.trim() === UNREPORTED_HEADCOUNT_TEXT;
/** @param {unknown} metric */
const isUnreportedHeadcount = (metric) => asMetric(metric)?.bucket === UNREPORTED_HEADCOUNT_BUCKET;

/**
 * @typedef {Object} Metric
 * @property {string} [bucket]
 * @property {number|null} [pointEstimate]
 * @property {number|null} [lowerBound]
 * @property {number|null} [upperBound]
 */

/** @param {unknown} value */
const isFiniteNumber = (value) => typeof value === "number" && Number.isFinite(value);

/** @param {unknown} metric @returns {Metric|null} */
const asMetric = (metric) => (metric && typeof metric === "object" ? /** @type {Metric} */ (metric) : null);

/**
 * `lower–upper`, `lower+` (no upper) or `<upper` (no lower) when a range is renderable;
 * otherwise falls back to pointEstimate (owner ruling Q1, FE #650: the range wins over
 * a point estimate whenever both are present).
 * @param {unknown} metric
 * @param {Intl.NumberFormat} numberFormatter
 * @returns {string|null}
 */
export const formatMetricRange = (metric, numberFormatter) => {
  const m = asMetric(metric);
  if (!m) return null;

  const hasLower = isFiniteNumber(m.lowerBound) && m.lowerBound > 0;
  const hasUpper = isFiniteNumber(m.upperBound);
  if (hasLower && hasUpper) {
    const formattedLower = numberFormatter.format(m.lowerBound);
    const formattedUpper = numberFormatter.format(m.upperBound);
    if (formattedLower === formattedUpper) return formattedLower;
    return `${formattedLower}${EN_DASH}${formattedUpper}`;
  }
  if (hasLower) return `${numberFormatter.format(m.lowerBound)}+`;
  if (hasUpper) return `<${numberFormatter.format(m.upperBound)}`;

  if (isFiniteNumber(m.pointEstimate)) return numberFormatter.format(m.pointEstimate);
  return null;
};

/** @param {unknown} metric @returns {string|null} */
export const formatRevenueBucket = (metric) => formatMetricRange(metric, DollarFormatter);

/** @param {unknown} metric @returns {string|null} */
export const formatHeadcountBucket = (metric) =>
  isUnreportedHeadcount(metric) ? null : formatMetricRange(metric, EmployeeFormatter);

/**
 * Sort key, kept consistent with what formatMetricRange displays (owner ruling Q1,
 * FE #650): lowerBound when a range is renderable (0 for an upper-only bucket), else
 * pointEstimate, else null (blank — sorts last). A wire-empty metric's lowerBound
 * defaults to 0 with no upperBound/pointEstimate; that is not a renderable range, so
 * it must not fall back to 0 (FE final review I-1) — it sorts last like any N/A row.
 * @param {unknown} metric
 * @returns {number|null}
 */
export const metricSortValue = (metric) => {
  const m = asMetric(metric);
  if (!m || isUnreportedHeadcount(m)) return null;

  const hasLower = isFiniteNumber(m.lowerBound) && m.lowerBound > 0;
  const hasUpper = isFiniteNumber(m.upperBound);
  if (hasLower || hasUpper) return hasLower ? m.lowerBound : 0;

  if (isFiniteNumber(m.pointEstimate)) return m.pointEstimate;
  return null;
};
